import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { HappyFall3D, HappyFallBallPose, HappyFallSceneFrame } from "./happy_fall_3d.js";
import { PlinkoLayout } from "./plinko_layout.js";
import { PlinkoBody, PlinkoDrop, PlinkoPhysics } from "./plinko_physics.js";

type BallState = "flight" | "enter" | "tunnel" | "fall" | "landed";

interface PlinkoSpin
{
  x: number;
  y: number;
  z: number;
}

interface PlinkoBall
{
  state: BallState;
  body: PlinkoBody;
  drop: PlinkoDrop;
  random: () => number;
  from_x: number;
  from_y: number;
  started_at: number;
  flight_ms: number;
  slot: number;
  label: string | null;
  on_land: () => void;
  spin: PlinkoSpin;
  spin_v: PlinkoSpin;
}

interface PlinkoFloat
{
  x: number;
  y: number;
  label: string;
  started_at: number;
}

export class PlinkoGame extends UIElement
{
  private static readonly MAX_FRAME_S = 0.05;
  private static readonly FLIGHT_SPEED = 6000;
  private static readonly FLIGHT_LIFT = 700;
  private static readonly ENTER_MS = 220;
  private static readonly ENTER_DRIFT = 50;
  private static readonly ENTER_MIN_SCALE = 0.3;
  private static readonly ENTER_MIN_SHADE = 0.15;
  private static readonly TUNNEL_MS = 60;
  private static readonly STACK_LOCK_Y = PlinkoLayout.FLOOR_Y - PlinkoLayout.BALL_RADIUS * 3;
  private static readonly SPIN_MIN_IMPACT = 60;
  private static readonly SPIN_MAX_IMPACT = 900;
  private static readonly ROLL_SHARE = 0.35;
  private static readonly SPIN_PER_IMPACT = 0.012;
  private static readonly SPIN_DAMPING = 2.5;
  private static readonly SHADOW_X = 6;
  private static readonly SHADOW_Y = 10;
  private static readonly FLOAT_MS = 900;
  private static readonly FLOAT_RISE = 110;
  private static readonly FLOAT_ABOVE_BALL = 70;
  private static readonly FLOAT_FONT = 80;
  private static readonly FLOAT_OUTLINE = 12;

  private readonly scale_factor: number;
  private readonly frame: HappyFallSceneFrame;

  private balls: PlinkoBall[] = [];
  private floats: PlinkoFloat[] = [];
  private aim_x: number = PlinkoLayout.BOARD_W / 2;
  private last_update = 0;
  private step_time = 0;
  private view: HappyFall3D | null = null;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, frame: HappyFallSceneFrame )
  {
    super( ctx, x, y, width, height, false, false );

    this.scale_factor = width / PlinkoLayout.BOARD_W;
    this.frame = frame;
    this.setClickable( false );
  }

  public setView( view: HappyFall3D ): void
  {
    this.view = view;
    this.forceRedraw();
  }

  public toDesign( local_x: number, local_y: number ): { x: number; y: number }
  {
    return { x: local_x / this.scale_factor, y: local_y / this.scale_factor };
  }

  public hasActiveBalls(): boolean
  {
    return this.balls.some( ball => ball.state !== "landed" ) || this.floats.length > 0;
  }

  public clearLanded(): void
  {
    this.balls = this.balls.filter( ball => ball.state !== "landed" );
    this.forceRedraw();
  }

  public aim( design_x: number ): void
  {
    this.aim_x = Math.max( PlinkoLayout.holeMinX(), Math.min( PlinkoLayout.holeMaxX(), design_x ) );
    this.forceRedraw();
  }

  public shoot( drop: PlinkoDrop, label: string | null, on_land: () => void, now: number ): void
  {
    this.aim( drop.x );

    const muzzle = this.muzzle();
    const distance = Math.hypot( drop.x - muzzle.x, PlinkoLayout.holeCenterY() - muzzle.y );

    this.balls.push( {
      state: "flight",
      body: { x: muzzle.x, y: muzzle.y, vx: 0, vy: 0, idle_ms: 0, impact: 0 },
      drop,
      random: PlinkoPhysics.random( drop.seed ),
      from_x: muzzle.x,
      from_y: muzzle.y,
      started_at: now,
      flight_ms: distance / PlinkoGame.FLIGHT_SPEED * 1000,
      slot: -1,
      label,
      on_land,
      spin: { x: 0, y: 0, z: 0 },
      spin_v: { x: 0, y: 0, z: 0 },
    } );
  }

  private cannonAngle(): number
  {
    const dx = this.aim_x - PlinkoLayout.CANNON_PIVOT_X;
    const dy = this.frame.cannon_y - PlinkoLayout.holeCenterY();
    const angle = Math.atan2( dx, dy );
    return Math.max( -PlinkoLayout.CANNON_MAX_ANGLE, Math.min( PlinkoLayout.CANNON_MAX_ANGLE, angle ) );
  }

  private muzzle(): { x: number; y: number }
  {
    const angle = this.cannonAngle();
    const length = PlinkoLayout.CANNON_LENGTH;
    return {
      x: PlinkoLayout.CANNON_PIVOT_X + Math.sin( angle ) * length,
      y: this.frame.cannon_y - Math.cos( angle ) * length,
    };
  }

  public update( now: number ): void
  {
    const frame_s = this.last_update === 0 ? 0 : Math.min( ( now - this.last_update ) / 1000, PlinkoGame.MAX_FRAME_S );
    this.last_update = now;

    if ( !this.hasActiveBalls() )
    {
      this.step_time = 0;
      return;
    }

    this.step_time += frame_s;
    while ( this.step_time >= PlinkoPhysics.STEP_S )
    {
      for ( const ball of this.balls )
      {
        if ( ball.state !== "fall" )
          continue;

        const landed = PlinkoPhysics.step( ball.body, ball.random ) || this.restsOnStack( ball );
        this.animateBall( ball );
        if ( landed )
          this.land( ball, now );
      }
      this.step_time -= PlinkoPhysics.STEP_S;
    }

    for ( const ball of this.balls )
      this.updatePhase( ball, now );

    this.floats = this.floats.filter( float => now - float.started_at < PlinkoGame.FLOAT_MS );

    this.forceRedraw();
  }

  private updatePhase( ball: PlinkoBall, now: number ): void
  {
    const elapsed = now - ball.started_at;

    if ( ball.state === "flight" )
      this.updateFlight( ball, now );
    else if ( ball.state === "enter" && elapsed >= PlinkoGame.ENTER_MS )
    {
      ball.state = "tunnel";
      ball.started_at = now;
    }
    else if ( ball.state === "tunnel" && elapsed >= PlinkoGame.TUNNEL_MS )
    {
      ball.state = "fall";
      ball.body = PlinkoPhysics.spawn( ball.drop, ball.random );
    }
  }

  private updateFlight( ball: PlinkoBall, now: number ): void
  {
    const t = Math.min( 1, ( now - ball.started_at ) / ball.flight_ms );
    ball.body.x = ball.from_x + ( ball.drop.x - ball.from_x ) * t;
    ball.body.y = ball.from_y + ( PlinkoLayout.holeCenterY() - ball.from_y ) * t;

    if ( t < 1 )
      return;

    ball.state = "enter";
    ball.started_at = now;
  }

  private animateBall( ball: PlinkoBall ): void
  {
    const dt = PlinkoPhysics.STEP_S;
    const body = ball.body;

    if ( body.impact > PlinkoGame.SPIN_MIN_IMPACT )
    {
      const kick = Math.min( body.impact, PlinkoGame.SPIN_MAX_IMPACT ) * PlinkoGame.SPIN_PER_IMPACT;
      ball.spin_v.x += ( Math.random() * 2 - 1 ) * kick;
      ball.spin_v.y += ( Math.random() * 2 - 1 ) * kick;
      ball.spin_v.z += ( Math.random() * 2 - 1 ) * kick;
    }

    const damping = Math.exp( -PlinkoGame.SPIN_DAMPING * dt );
    const roll = PlinkoGame.ROLL_SHARE / PlinkoLayout.BALL_RADIUS;
    ball.spin_v.x *= damping;
    ball.spin_v.y *= damping;
    ball.spin_v.z *= damping;
    ball.spin.x += ( body.vy * roll + ball.spin_v.x ) * dt;
    ball.spin.y += ( body.vx * roll + ball.spin_v.y ) * dt;
    ball.spin.z += ( -body.vx * roll + ball.spin_v.z ) * dt;
  }

  private stackSize( slot: number ): number
  {
    return this.balls.filter( ball => ball.state === "landed" && ball.slot === slot ).length;
  }

  private restY( below: number ): number
  {
    return PlinkoLayout.FLOOR_Y - PlinkoLayout.BALL_RADIUS * ( 1 + below * 2 );
  }

  private restsOnStack( ball: PlinkoBall ): boolean
  {
    const below = this.stackSize( PlinkoLayout.slotIndex( ball.body.x ) );
    return below > 0 && ball.body.y >= Math.max( this.restY( below ), PlinkoGame.STACK_LOCK_Y );
  }

  private land( ball: PlinkoBall, now: number ): void
  {
    const slot = PlinkoLayout.slotIndex( ball.body.x );

    ball.body.y = this.restY( this.stackSize( slot ) );
    ball.slot = slot;
    ball.state = "landed";
    ball.on_land();

    if ( !ball.label )
      return;

    this.floats.push( { x: PlinkoLayout.slotCenterX( slot ), y: ball.body.y - PlinkoGame.FLOAT_ABOVE_BALL, label: ball.label, started_at: now } );
  }

  private drawFloat( ctx: CanvasRenderingContext2D, float: PlinkoFloat, now: number ): void
  {
    const t = ( now - float.started_at ) / PlinkoGame.FLOAT_MS;
    const y = float.y - PlinkoGame.FLOAT_RISE * t;

    ctx.save();
    ctx.globalAlpha *= Math.max( 0, 1 - t * t );
    ctx.font = `900 ${PlinkoGame.FLOAT_FONT}px ${TextStyleScheme.rubik}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = PlinkoGame.FLOAT_OUTLINE;
    ctx.strokeStyle = ColorScheme.happy_fall_value_outline;
    ctx.strokeText( float.label, float.x, y );
    ctx.fillStyle = ColorScheme.happy_fall_reward;
    ctx.fillText( float.label, float.x, y );
    ctx.restore();
  }

  private pose( ball: PlinkoBall, now: number ): HappyFallBallPose
  {
    const pose = { x: ball.body.x, y: ball.body.y, z: 0, rx: ball.spin.x, ry: ball.spin.y, rz: ball.spin.z, scale: 1, shade: 1 };

    if ( ball.state === "flight" )
      pose.z = PlinkoGame.FLIGHT_LIFT * Math.sin( Math.PI * Math.min( 1, ( now - ball.started_at ) / ball.flight_ms ) );
    else if ( ball.state === "enter" )
      this.enterPose( ball, pose, now );

    return pose;
  }

  private enterPose( ball: PlinkoBall, pose: HappyFallBallPose, now: number ): void
  {
    const t = Math.min( 1, ( now - ball.started_at ) / PlinkoGame.ENTER_MS );
    const dx = ball.drop.x - ball.from_x;
    const dy = PlinkoLayout.holeCenterY() - ball.from_y;
    const length = Math.hypot( dx, dy ) || 1;
    const drift = PlinkoGame.ENTER_DRIFT * ( 1 - ( 1 - t ) * ( 1 - t ) );
    const roll = drift / PlinkoLayout.BALL_RADIUS / length;

    pose.x += dx / length * drift;
    pose.y -= dy / length * drift;
    pose.rx = -dy * roll;
    pose.ry = dx * roll;
    pose.scale = 1 - ( 1 - PlinkoGame.ENTER_MIN_SCALE ) * t * t;
    pose.shade = 1 - ( 1 - PlinkoGame.ENTER_MIN_SHADE ) * t;
  }

  private holePath( ctx: CanvasRenderingContext2D, inset: number ): void
  {
    const w = PlinkoLayout.HOLE_W - inset * 2;
    const h = PlinkoLayout.HOLE_H - inset * 2;
    ctx.roundRect( PlinkoLayout.HOLE_X + inset, PlinkoLayout.HOLE_Y + inset, w, h, h / 2 );
  }

  private drawScene( ctx: CanvasRenderingContext2D, image: HTMLCanvasElement ): void
  {
    ctx.drawImage( image, this.frame.x, this.frame.y, this.frame.w, this.frame.h );
  }

  private drawShadow( ctx: CanvasRenderingContext2D, ball: PlinkoBall ): void
  {
    const x = ball.body.x + PlinkoGame.SHADOW_X;
    const y = ball.body.y + PlinkoGame.SHADOW_Y;
    const radius = PlinkoLayout.BALL_RADIUS;

    const gradient = ctx.createRadialGradient( x, y, 0, x, y, radius );
    gradient.addColorStop( 0, ColorScheme.happy_fall_ball_shadow );
    gradient.addColorStop( 1, ColorScheme.happy_fall_ball_shadow_edge );

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc( x, y, radius, 0, Math.PI * 2 );
    ctx.fill();
  }

  private drawBoardBalls( ctx: CanvasRenderingContext2D, view: HappyFall3D, now: number ): void
  {
    const balls = this.balls.filter( ball => ball.state === "fall" || ball.state === "landed" );

    ctx.save();
    ctx.beginPath();
    ctx.rect( this.frame.x, this.frame.y, this.frame.w, this.frame.h );
    this.holePath( ctx, -PlinkoLayout.HOLE_LINE / 2 );
    ctx.clip( "evenodd" );
    for ( const ball of balls )
      this.drawShadow( ctx, ball );
    this.drawScene( ctx, view.render( balls.map( ball => this.pose( ball, now ) ) ) );
    ctx.restore();
  }

  private drawView( ctx: CanvasRenderingContext2D, now: number ): void
  {
    const view = this.view;
    if ( !view )
      return;

    view.setCannonAngle( this.cannonAngle() );
    this.drawBoardBalls( ctx, view, now );

    const flying = this.balls.filter( ball => ball.state === "flight" );
    if ( flying.length > 0 )
      this.drawScene( ctx, view.render( flying.map( ball => this.pose( ball, now ) ), false ) );

    const entering = this.balls.filter( ball => ball.state === "enter" );
    if ( entering.length > 0 )
      this.drawEnteringBalls( ctx, view, entering, now );
  }

  private drawEnteringBalls( ctx: CanvasRenderingContext2D, view: HappyFall3D, balls: PlinkoBall[], now: number ): void
  {
    ctx.save();
    ctx.beginPath();
    this.holePath( ctx, PlinkoLayout.HOLE_LINE / 2 );
    ctx.clip();
    this.drawScene( ctx, view.render( balls.map( ball => this.pose( ball, now ) ), false ) );
    ctx.restore();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const ctx = this.ctx;
    const now = performance.now();

    ctx.save();
    ctx.translate( -this.rect.getW2(), -this.rect.getH2() );
    ctx.scale( this.scale_factor, this.scale_factor );

    this.drawView( ctx, now );

    for ( const float of this.floats )
      this.drawFloat( ctx, float, now );

    ctx.restore();

    this.finishRender();
  }

  public name(): string
  {
    return "PlinkoGame";
  }
}
